import { memo, useCallback, useRef, useState } from 'react';
import { Box } from '@mentimeter/ragnar-ui/box';
import { Hotkey } from '../../../../shared/components/Hotkey';
import type { OpenEndedResponse } from '../types';
import { ResponsesGrid } from './responses-grid';

interface SpeechBubblesProps {
  responses: OpenEndedResponse[];
  showUpvotes: boolean;
  shouldPaginate: boolean;
  onItemClick: (index: number) => void;
  numberOfColumns: number;
  shouldAnimate: boolean;
  isResponsesHidden: boolean;
  isReadonly: boolean;
}

const SCROLL_AMOUNT = 150;
const PAGE_SIZE = 30; // lazy load responses

const getPagination = (
  shouldPaginate: boolean,
  totalItemsCount: number,
  page: number,
) => {
  if (shouldPaginate) {
    return [0, page * PAGE_SIZE + PAGE_SIZE];
  }

  return [0, totalItemsCount];
};

const SCROLL_PADDING = 32;

export const SpeechBubbles = memo<SpeechBubblesProps>(
  ({
    responses,
    showUpvotes,
    shouldPaginate,
    numberOfColumns,
    onItemClick,
    shouldAnimate,
    isResponsesHidden,
    isReadonly,
  }: SpeechBubblesProps) => {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const [currentPage, setCurrentPage] = useState(0);
    const totalPageCount = Math.ceil(responses.length / PAGE_SIZE);

    // Set visible items
    const [fromIndex, toIndex] = getPagination(
      shouldPaginate,
      responses.length,
      currentPage,
    );
    const visibleItems = responses.slice(fromIndex, toIndex);

    // Load "x" items whenever bottom of container is reached
    const lazyLoadItems = useCallback(() => {
      if (!containerRef.current) return;
      if (currentPage >= totalPageCount - 1) return;

      const {
        offsetHeight = 0,
        scrollHeight = 0,
        scrollTop = 0,
      } = containerRef.current;

      if (scrollTop + SCROLL_PADDING > scrollHeight - offsetHeight) {
        const nextPage = currentPage + 1;
        if (nextPage + 1 === totalPageCount - 1) {
          setCurrentPage(totalPageCount - 1);
        } else {
          setCurrentPage(nextPage);
        }
      }
    }, [currentPage, totalPageCount]);

    // Scrolling with up and down arrows
    const handleHotkeyScroll = (direction: 'up' | 'down') => {
      if (!containerRef.current) return;

      containerRef.current.scrollTop =
        direction === 'up'
          ? containerRef.current.scrollTop - SCROLL_AMOUNT
          : containerRef.current.scrollTop + SCROLL_AMOUNT;

      lazyLoadItems();
    };

    return (
      <Box
        width="100%"
        height="100%"
        data-testid="visualization-speech-bubbles"
      >
        <Hotkey keyCode="arrowup" action={() => handleHotkeyScroll('up')} />
        <Hotkey keyCode="arrowdown" action={() => handleHotkeyScroll('down')} />
        <Box
          ref={containerRef}
          flexDirection="column"
          width="100%"
          height="100%"
          overflow="auto"
          onScroll={lazyLoadItems}
          extend={() => ({
            scrollBehavior: 'smooth',
          })}
        >
          <ResponsesGrid
            responses={visibleItems}
            onItemClick={onItemClick}
            shouldSplitResponses={showUpvotes}
            numberOfColumns={numberOfColumns}
            shouldAnimate={shouldAnimate}
            isResponsesHidden={isResponsesHidden}
            isReadonly={isReadonly}
          />
        </Box>
      </Box>
    );
  },
);
